import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    SreEntityType,
    SreInflightSearchQuery,
    SreInflightSearchResponse,
} from '../../../../../AGSS/models/SreHealth';
import { searchSreInflight } from '../../../../../AGSS/api/AGRMApi';
import { readSreCache, sreCacheScope, writeSreCache } from './sreSessionCache';

const DEBOUNCE_MS = 300;

export interface SreTriageQueryState {
    data: SreInflightSearchResponse | null;
    isLoading: boolean;
    error: string | null;
    lastUpdated: Date | null;
    refresh: () => void;
}

interface QueryEntry {
    key: string;
    data: SreInflightSearchResponse | null;
    error: string | null;
    isLoading: boolean;
    updatedAt: number | null;
}

export const useSreTriageQuery = (
    type: SreEntityType,
    query: SreInflightSearchQuery,
    enabled: boolean
): SreTriageQueryState => {
    const querySignature = JSON.stringify(query);
    const key = `${sreCacheScope()}:${type}:search:${querySignature}`;
    const [entry, setEntry] = useState<QueryEntry | null>(null);
    const [refreshToken, setRefreshToken] = useState(0);

    useEffect(() => {
        if (!enabled) return;
        const cached = readSreCache<SreInflightSearchResponse>(key);
        const source = axios.CancelToken.source();
        let cancelled = false;
        setEntry({
            key,
            data: cached?.data ?? null,
            error: null,
            isLoading: true,
            updatedAt: cached?.savedAt ?? null,
        });

        const timer = window.setTimeout(async () => {
            try {
                const result = await searchSreInflight(
                    type,
                    JSON.parse(querySignature) as SreInflightSearchQuery,
                    source.token
                );

                if (!cancelled) {
                    writeSreCache(key, result);
                    setEntry({
                        key,
                        data: result,
                        error: null,
                        isLoading: false,
                        updatedAt: Date.now(),
                    });
                }
            } catch (err) {
                if (axios.isCancel(err) || cancelled) return;
                const asAny = err as any;
                setEntry({
                    key,
                    data: null,
                    isLoading: false,
                    updatedAt: null,
                    error:
                        asAny?.response?.data?.message ??
                        asAny?.message ??
                        'Failed to search in-flight orchestrations.',
                });
            }
        }, DEBOUNCE_MS);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
            source.cancel('Query changed');
        };
    }, [type, querySignature, key, refreshToken, enabled]);

    const refresh = useCallback(() => setRefreshToken((t) => t + 1), []);
    const current = entry?.key === key && enabled ? entry : null;
    return {
        data: current?.data ?? null,
        isLoading: enabled && (current?.isLoading ?? true),
        error: current?.error ?? null,
        lastUpdated: current?.updatedAt ? new Date(current.updatedAt) : null,
        refresh,
    };
};
